import {
  Component,
  Input,
  ChangeDetectionStrategy,
  booleanAttribute,
} from '@angular/core';

export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

const PADDING_MAP: Record<CardPadding, string> = {
  none: '',
  sm:   'p-3',
  md:   'p-5',
  lg:   'p-6',
};

/**
 * Card base do design system.
 * Usa slots via <ng-content select="..."> para máxima flexibilidade.
 *
 * @usage
 * <!-- Card simples -->
 * <ui-card>Conteúdo</ui-card>
 *
 * <!-- Card com header -->
 * <ui-card>
 *   <ng-container slot="header">
 *     <h3 class="text-sm font-semibold text-gray-700">Título</h3>
 *   </ng-container>
 *   Conteúdo do corpo
 * </ui-card>
 *
 * <!-- Card KPI clicável -->
 * <ui-card [hover]="true" [noPadding]="false" padding="md">
 *   ...
 * </ui-card>
 */
@Component({
  selector: 'ui-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block',
    '[attr.data-card]': '"true"',
  },
  template: `
    <div [class]="cardClass">

      <!-- Header slot — renderizado somente se projetado -->
      <div #headerSlot>
        <ng-content select="[slot=header]" />
      </div>

      <!-- Divisor entre header e body quando header existe -->
      <ng-content select="[slot=divider]" />

      <!-- Body — padding controlado por @Input() padding -->
      <div [class]="bodyClass">
        <ng-content />
      </div>

      <!-- Footer slot -->
      <ng-content select="[slot=footer]" />
    </div>
  `,
})
export class UiCardComponent {
  /** Padding do body */
  @Input() padding: CardPadding = 'md';

  /** Sombra elevada (para cards flutuantes, modais, dropdowns) */
  @Input({ transform: booleanAttribute }) elevated = false;

  /** Estado hover com sombra e cursor pointer (KPI cards clicáveis) */
  @Input({ transform: booleanAttribute }) hover = false;

  /** Remove padding do body (útil para tabelas dentro do card) */
  @Input({ transform: booleanAttribute }) noPadding = false;

  /** Borda colorida à esquerda (indicador de status) */
  @Input() accent?: 'success' | 'warning' | 'danger' | 'info' | 'brand';

  get cardClass(): string {
    const accentMap: Record<string, string> = {
      success: 'border-l-4 border-l-emerald-500',
      warning: 'border-l-4 border-l-amber-500',
      danger:  'border-l-4 border-l-rose-500',
      info:    'border-l-4 border-l-blue-500',
      brand:   'border-l-4 border-l-brand-600',
    };

    return [
      'bg-white rounded-xl border border-gray-200 overflow-hidden',
      this.elevated ? 'shadow-[var(--shadow-float)]'
                    : 'shadow-[var(--shadow-card)]',
      this.hover ? 'hover:shadow-[var(--shadow-float)] transition-shadow duration-200 cursor-pointer'
                 : '',
      this.accent ? accentMap[this.accent] : '',
    ].filter(Boolean).join(' ');
  }

  get bodyClass(): string {
    return this.noPadding ? '' : PADDING_MAP[this.padding];
  }
}
